import { type FormEvent, useState } from "react";
import { Navigate } from "react-router";
import type { WikiTemplateDTO } from "@justone/shared";
import { errorMessage, fieldError } from "../api/http";
import { useMe } from "../api/queries";
import { type TemplateBody, useTemplateMutations, useWikiTemplates } from "../api/wiki";
import { Field, useToast } from "../components/ui";
import { PageShell } from "./OtherPages";

const EMPTY: TemplateBody = { name: "", description: "", title: "", markdown: "" };

/** 관리자: 새 페이지 템플릿. 기본 템플릿은 고칠 수 없고, 더 만들 수 있다 */
export function WikiTemplatesPage() {
  const me = useMe();
  const { data: templates = [] } = useWikiTemplates();
  const ops = useTemplateMutations();
  const toast = useToast();
  const [editing, setEditing] = useState<{ id: number | null; body: TemplateBody } | null>(null);
  if (me.role !== "admin") return <Navigate to="/" replace />;

  const remove = (t: WikiTemplateDTO) => {
    const body = { name: t.name, description: t.description, title: t.title, markdown: t.markdown };
    ops.remove.mutate(t.id!, {
      onSuccess: () => toast(`"${t.name}" 템플릿을 지웠습니다.`, { label: "되돌리기", run: () => ops.create.mutate(body, { onError: (e) => toast(errorMessage(e)) }) }),
      onError: (e) => toast(errorMessage(e)),
    });
  };

  return (
    <PageShell title="위키 템플릿">
      <p className="view-intro">
        새 페이지를 만들 때 고르는 틀입니다. 내용과 제목에 <code>{"{{날짜}}"}</code> <code>{"{{요일}}"}</code> <code>{"{{작성자}}"}</code>를 쓰면 만들 때 바꿔 넣습니다. 편집기에서 <code>/템플릿</code>으로 넣을 수도 있습니다.
      </p>
      <ul className="template-admin">
        {templates.map((t) => (
          <li key={t.key}>
            <span className="grow">
              <strong>{t.name}</strong>
              {t.builtin && <span className="pill">기본</span>}
              {t.description && <span className="muted"> · {t.description}</span>}
            </span>
            {!t.builtin && (
              <>
                <button type="button" className="btn btn-sm" onClick={() => setEditing({ id: t.id, body: { name: t.name, description: t.description, title: t.title, markdown: t.markdown } })}>
                  고치기
                </button>
                <button type="button" className="btn btn-sm btn-quiet" onClick={() => remove(t)}>
                  지우기
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {editing ? (
        <TemplateForm key={editing.id ?? "new"} id={editing.id} initial={editing.body} onDone={() => setEditing(null)} />
      ) : (
        <button type="button" className="btn btn-primary" onClick={() => setEditing({ id: null, body: EMPTY })}>
          새 템플릿
        </button>
      )}
    </PageShell>
  );
}

function TemplateForm({ id, initial, onDone }: { id: number | null; initial: TemplateBody; onDone: () => void }) {
  const [body, setBody] = useState(initial);
  const [err, setErr] = useState<unknown>(null);
  const ops = useTemplateMutations();
  const toast = useToast();
  const set = (k: keyof TemplateBody) => (e: { target: { value: string } }) => setBody((b) => ({ ...b, [k]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const done = () => {
      toast(id ? "템플릿을 고쳤습니다." : "템플릿을 만들었습니다.");
      onDone();
    };
    if (id) ops.update.mutate({ id, ...body }, { onSuccess: done, onError: setErr });
    else ops.create.mutate(body, { onSuccess: done, onError: setErr });
  };
  return (
    <form className="template-form" onSubmit={submit}>
      <h2>{id ? "템플릿 고치기" : "새 템플릿"}</h2>
      {err && !fieldError(err, "name") ? <p className="form-error">{errorMessage(err)}</p> : null}
      <Field id="tpl-name" label="이름" error={fieldError(err, "name")}>
        <input id="tpl-name" className="input" required maxLength={40} value={body.name} onChange={set("name")} autoFocus />
      </Field>
      <Field id="tpl-desc" label="설명 (선택)">
        <input id="tpl-desc" className="input" maxLength={200} value={body.description} onChange={set("description")} placeholder="예: 주간 회의 안건과 결정 사항" />
      </Field>
      <Field id="tpl-title" label="새 페이지 제목 (선택)" error={fieldError(err, "title")}>
        <input id="tpl-title" className="input" maxLength={120} value={body.title} onChange={set("title")} placeholder="예: {{날짜}} 주간 회의" />
      </Field>
      <Field id="tpl-md" label="내용 (마크다운)">
        <textarea id="tpl-md" className="input mono" rows={14} value={body.markdown} onChange={set("markdown")} spellCheck={false} />
      </Field>
      <div className="buttons">
        <button type="button" className="btn" onClick={onDone}>
          취소
        </button>
        <button type="submit" className="btn btn-primary" disabled={ops.create.isPending || ops.update.isPending}>
          저장
        </button>
      </div>
    </form>
  );
}
